// Zero-network fallback thumbnail for course cards.
// If a hotlinked Unsplash image ever 404s, times out, or is blocked, the
// <img onError> handler swaps to this inline SVG data URI instead of
// showing the browser's broken-image icon. Because it's a data URI (not a
// URL), it can never itself fail to load.

const escapeXml = (s = "") =>
  String(s).replace(/[<>&'"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", "'": "&apos;", '"': "&quot;" }[c]));

// Simple deterministic hash so the same title always gets the same accent motif.
const hashCode = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

export function buildFallbackImage(title = "IIUTC Study", subtitle = "Course") {
  const seed = hashCode(title);
  const angle = 20 + (seed % 40); // 20–60deg diagonal
  const t = escapeXml(title.length > 34 ? `${title.slice(0, 33)}…` : title);
  const sub = escapeXml(subtitle);

  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 900 600" role="img" aria-label="${t}">
  <defs>
    <linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%" gradientTransform="rotate(${angle})">
      <stop offset="0%" stop-color="#0A3161"/>
      <stop offset="55%" stop-color="#123a72"/>
      <stop offset="100%" stop-color="#B31942"/>
    </linearGradient>
    <pattern id="dots" width="34" height="34" patternUnits="userSpaceOnUse">
      <circle cx="2" cy="2" r="1.6" fill="rgba(255,255,255,0.14)"/>
    </pattern>
  </defs>
  <rect width="900" height="600" fill="url(#g)"/>
  <rect width="900" height="600" fill="url(#dots)"/>
  <circle cx="770" cy="90" r="150" fill="rgba(255,255,255,0.06)"/>
  <circle cx="90" cy="520" r="120" fill="rgba(255,255,255,0.06)"/>
  <g transform="translate(60,250)">
    <rect x="0" y="0" width="64" height="64" rx="16" fill="rgba(255,255,255,0.14)"/>
    <path d="M14 30 L32 20 L50 30 L32 40 Z" fill="#ffffff"/>
    <path d="M22 34.5 V44 C22 47 27 49 32 49 C37 49 42 47 42 44 V34.5 L32 40 Z" fill="#ffffff" opacity="0.85"/>
  </g>
  <text x="60" y="345" font-family="Arial, Helvetica, sans-serif" font-size="30" font-weight="700" fill="#ffffff">${t}</text>
  <text x="60" y="375" font-family="Arial, Helvetica, sans-serif" font-size="18" fill="rgba(255,255,255,0.75)">IIUTC Study · ${sub}</text>
</svg>`.trim();

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/**
 * Attach to an <img>'s onError to swap in the branded fallback exactly once
 * (the null-out on onerror prevents any retry loop if the data URI itself
 * were ever to fail, which it structurally cannot).
 */
export function handleImageError(e, title, subtitle) {
  const el = e.currentTarget;
  el.onerror = null;
  el.src = buildFallbackImage(title, subtitle);
}
